// Modified, Object-Oriented version of pointer lock controls from three.js fps example: https://github.com/mrdoob/three.js/blob/master/examples/games_fps.html

import { PointerLockControls } from "three/examples/jsm/controls/PointerLockControls.js";
import { Capsule } from "three/examples/jsm/math/Capsule.js";
import { Octree } from "three/examples/jsm/math/Octree.js";
import { OctreeHelper } from "three/examples/jsm/helpers/OctreeHelper.js";
import * as THREE from "three";
import { LAYERS } from "@/three/constants";

// TODO: Put these in external module
const CAMERA_HEIGHT = 3.6;
const PLAYER_RADIUS = 0.35;
const PLAYER_BOTTOM = 0.35;
const STARTING_HEIGHT = 2;
const GRAVITY = 30;
const CONTROL_SPEED_GROUND = 50;
const CONTROL_SPEED_AIR = 8;
const CONTROL_SPEED_GROUND_DEBUG = 100;
const ARROW_HEIGHT = 95;
const MINIMUM_Y_COORDINATE = -100; // Reset position if player falls below this

export default class FirstPersonControls extends PointerLockControls {
  /**
   * Creates a new FirstPersonControls object
   * @param {*} camera camera object to attach pointer lock controls to
   * @param {*} domElement DOM element to attach pointer lock controls to
   * @param {*} terrain terrain object to detect collisions with
   * @param {*} onLock callback function when the pointer is locked
   * @param {*} onUnlock callback function when the pointer is unlocked
   * @param {*} scene scene to add debug objects to
   * @param {*} debugMode whether or not to enable debug mode controls
   * @param {*} position initial position of the camera
   * @param {*} rotation initial rotation of the camera
   */
  constructor(
    camera,
    domElement,
    terrain,
    onLock = null,
    onUnlock = null,
    scene,
    debugMode = false,
    position = new THREE.Vector3(0, 0, 0),
    rotation = new THREE.Euler(0, 0, 0)
  ) {
    super(camera, domElement);
    this.onLock = onLock;
    this.onUnlock = onUnlock;
    this.terrain = terrain;
    this.debugMode = debugMode;
    this.initialPosition = position;
    this.initialRotation = rotation;

    // whether or not the player has input to move in a direction
    this.movingForward = false;
    this.movingBackward = false;
    this.movingLeft = false;
    this.movingRight = false;

    this.playerOnFloor = false;
    // current velocity of the player
    this.velocity = new THREE.Vector3();
    // used for temporary direction calculations
    this.direction = new THREE.Vector3();
    // capsule representing the player, used for collision detection
    this.playerCollider = new Capsule();

    // Player positioning
    // Rotate camera to initial rotation
    camera.rotation.copy(this.initialRotation);
    // Add starting height to initial position
    this.initialPosition.y += STARTING_HEIGHT;
    // Set camera and collider position to initial position
    this.setPlayerPosition(this.initialPosition);

    // create octree from terrain for detecting collisions with terrain
    this.worldOctree = new Octree();
    this.worldOctree.fromGraphNode(terrain);
    const helper = new OctreeHelper(this.worldOctree);
    helper.visible = debugMode;
    scene.add(helper);

    this.addEventListener("lock", () => {
      if (this.onLock) this.onLock();
    });

    this.addEventListener("unlock", () => {
      if (this.onUnlock) this.onUnlock();
    });

    this.onKeyDown = this.onKeyDown.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    document.addEventListener("keydown", this.onKeyDown);
    document.addEventListener("keyup", this.onKeyUp);

    // Add player direction arrow
    // TODO: add to separate layer so not visible in first person camera
    const arrowGeometry = new THREE.ConeGeometry(3, 10, 15);
    const arrowMaterial = new THREE.MeshBasicMaterial({ color: 0xffff00 });
    this.arrow = new THREE.Mesh(arrowGeometry, arrowMaterial);
    // Make arrow point forward
    this.arrow.rotation.x = 1.5 * Math.PI;
    // Make arrow only visible in minimap (simple geometry has no children to traverse)
    this.arrow.layers.set(LAYERS.MINI_MAP_ONLY);
    scene.add(this.arrow);

    this.updateCameraPosition();
  }

  /**
   * Sets the position of the player collider
   * @param {*} position
   */
  setPlayerPosition(position) {
    // NOTE: camera will copy position from playerCollider in `update` loop
    this.playerCollider.set(
      new THREE.Vector3(position.x, position.y + PLAYER_BOTTOM, position.z),
      new THREE.Vector3(position.x, position.y + CAMERA_HEIGHT, position.z),
      PLAYER_RADIUS
    );
  }

  /**
   * Resets the position and rotation of the camera to the initial position and rotation
   */
  resetPosition() {
    this.setPlayerPosition(this.initialPosition);
  }

  /**
   * Updates the position of the camera based on the time elapsed, controls, collisions, and current velocity
   * @param {*} delta time since last update
   */
  update(delta) {
    if (this.isLocked === true) {
      if (this.getObject().position.y < MINIMUM_Y_COORDINATE) {
        this.resetPosition();
      }
      this.updateControls(delta);
      this.updatePlayer(delta);
    }
  }

  /**
   * Updates the velocity of the player based on the current controls
   * @param {*} delta time since last update
   */
  updateControls(delta) {
    // gives a bit of air control
    const groundSpeed = this.debugMode
      ? CONTROL_SPEED_GROUND_DEBUG
      : CONTROL_SPEED_GROUND;
    const speedDelta =
      delta * (this.playerOnFloor ? groundSpeed : CONTROL_SPEED_AIR);

    if (this.movingForward) {
      this.velocity.add(this.getForwardVector().multiplyScalar(speedDelta));
    }

    if (this.movingBackward) {
      this.velocity.add(this.getForwardVector().multiplyScalar(-speedDelta));
    }

    if (this.movingLeft) {
      this.velocity.add(this.getSideVector().multiplyScalar(-speedDelta));
    }

    if (this.movingRight) {
      this.velocity.add(this.getSideVector().multiplyScalar(speedDelta));
    }
  }

  /**
   * Returns a vector representing the side direction, relative to where the camera is facing
   * @returns THREE.Vector3 representing the side direction
   */
  getSideVector() {
    this.camera.getWorldDirection(this.direction);
    this.direction.y = 0;
    this.direction.normalize();
    this.direction.cross(this.camera.up);

    return this.direction;
  }

  /**
   * Returns a vector representing the forward direction, relative to where the camera is facing
   * @returns THREE.Vector3 representing the forward direction
   */
  getForwardVector() {
    this.camera.getWorldDirection(this.direction);
    this.direction.y = 0;
    this.direction.normalize();

    return this.direction;
  }

  /**
   * Updates the position of the player based on the current velocity and collisions
   * @param {*} delta time since last update
   */
  updatePlayer(delta) {
    let damping = Math.exp(-4 * delta) - 1;

    if (!this.playerOnFloor) {
      this.velocity.y -= GRAVITY * delta;
      // small air resistance
      damping *= 0.1;
    }

    this.velocity.addScaledVector(this.velocity, damping);

    const deltaPosition = this.velocity.clone().multiplyScalar(delta);
    this.playerCollider.translate(deltaPosition);

    this.playerCollisions();

    this.updateCameraPosition();
  }

  updateCameraPosition() {
    this.getObject().position.copy(this.playerCollider.end);
    // Update arrow position and rotation to match the player
    this.arrow.position.set(
      this.playerCollider.end.x,
      ARROW_HEIGHT,
      this.playerCollider.end.z
    );
    // Make arrow look in same direction as camera on xz plane
    this.getDirection(this.direction);
    const theta = Math.atan2(this.direction.x, this.direction.z);
    this.arrow.rotation.z = theta + Math.PI;
  }

  playerCollisions() {
    const result = this.worldOctree.capsuleIntersect(this.playerCollider);

    this.playerOnFloor = false;

    if (result) {
      this.playerOnFloor = result.normal.y > 0;

      if (!this.playerOnFloor) {
        this.velocity.addScaledVector(
          result.normal,
          -result.normal.dot(this.velocity)
        );
      }

      this.playerCollider.translate(result.normal.multiplyScalar(result.depth));
    }
  }

  onKeyUp(event) {
    switch (event.code) {
      case "ArrowUp":
      case "KeyW":
        this.movingForward = false;
        break;

      case "ArrowLeft":
      case "KeyA":
        this.movingLeft = false;
        break;

      case "ArrowDown":
      case "KeyS":
        this.movingBackward = false;
        break;

      case "ArrowRight":
      case "KeyD":
        this.movingRight = false;
        break;
    }
  }

  onKeyDown(event) {
    switch (event.code) {
      case "ArrowUp":
      case "KeyW":
        this.movingForward = true;
        break;

      case "ArrowLeft":
      case "KeyA":
        this.movingLeft = true;
        break;

      case "ArrowDown":
      case "KeyS":
        this.movingBackward = true;
        break;

      case "ArrowRight":
      case "KeyD":
        this.movingRight = true;
        break;

      case "Space":
        if (this.playerOnFloor || this.debugMode) {
          this.velocity.y += 15;
        }
        break;
    }
  }
}
